import { ClipboardList } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useCaseService from '../../services/useCaseService';
import { UseCaseSummary, UseCaseStatus, Domain } from '../../types';
import { domainService } from '../../services/domainService';
import { PageLayout } from '../../components/Layout/PageLayout';
import { UseCasesActionsHeader } from '../../components/UseCases/UseCasesActionsHeader';
import { UseCaseTable } from '../../components/UseCases/UseCaseTable';

const UseCasesList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const uncategorizedLabel = t('useCases.uncategorized');

  // Filters
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | UseCaseStatus>('all');
  const [categoryFilter, setCategoryFilter] = useState<string | 'all'>('all');

  // Data
  const [useCases, setUseCases] = useState<UseCaseSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const domainNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of domains) m[d.id] = d.name;
    return m;
  }, [domains]);

  // Derived data: categories present in use cases (for filters and dashboard tabs)
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const uc of useCases) {
      const raw = (uc.category || '').trim();
      const key = raw || uncategorizedLabel;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [useCases, uncategorizedLabel]);

  const sortedCategories = useMemo(
    () => Object.entries(categoryCounts).sort((a, b) => a[0].localeCompare(b[0])),
    [categoryCounts],
  );

  // Filters panel
  const [filterDomainId, setFilterDomainId] = useState<string | null>(null);
  const [valueMin, setValueMin] = useState<number | ''>('');
  const [valueMax, setValueMax] = useState<number | ''>('');
  const [complexityMin, setComplexityMin] = useState<number | ''>('');
  const [complexityMax, setComplexityMax] = useState<number | ''>('');

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await useCaseService.list();
        if (!ignore) setUseCases(data);
        // Domains map for display
        const userDomains = await domainService.getUserDomains();
        if (!ignore) setDomains(userDomains);
      } catch (e: any) {
        if (!ignore) setError(e?.message || t('useCases.table.error'));
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return useCases.filter((u) => {
      const matchQuery = u.name.toLowerCase().includes(q) || (u.short_description || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || u.status === statusFilter;
      const matchDomain = !filterDomainId || (u as any).domain_id === filterDomainId;
      const matchCategory =
        categoryFilter === 'all' ||
        ((u.category || uncategorizedLabel).trim() || uncategorizedLabel) === categoryFilter;
      const v = (u.value_score ?? undefined) as number | undefined;
      const c = (u.complexity_score ?? undefined) as number | undefined;
      const matchValue = (
        (valueMin === '' || (typeof v === 'number' && v >= valueMin)) &&
        (valueMax === '' || (typeof v === 'number' && v <= valueMax))
      );
      const matchComplexity = (
        (complexityMin === '' || (typeof c === 'number' && c >= complexityMin)) &&
        (complexityMax === '' || (typeof c === 'number' && c <= complexityMax))
      );
      return matchQuery && matchStatus && matchDomain && matchCategory && matchValue && matchComplexity;
    });
  }, [useCases, query, statusFilter, filterDomainId, categoryFilter, valueMin, valueMax, complexityMin, complexityMax, uncategorizedLabel]);

  // Derived metrics for category filter display
  const totalUseCases = filtered.length;

  // Selection state for multi-selection prioritization
  const [selectedUseCaseIds, setSelectedUseCaseIds] = useState<Set<string>>(new Set());
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Get eligible use cases for prioritization (Identified or Qualified status)
  const eligibleForPrioritization = useMemo(() => {
    return filtered.filter((uc) => uc.status === 'Identified' || uc.status === 'Qualified');
  }, [filtered]);

  // Handle checkbox selection
  const handleToggleSelection = (useCaseId: string) => {
    setSelectedUseCaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(useCaseId)) {
        next.delete(useCaseId);
      } else {
        next.add(useCaseId);
      }
      return next;
    });
  };

  // Handle select all eligible
  const handleSelectAllEligible = () => {
    const allEligibleIds = new Set(eligibleForPrioritization.map((uc) => uc.id));
    setSelectedUseCaseIds(allEligibleIds);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedUseCaseIds(new Set());
  };

  // Handle bulk prioritization
  const handleMarkAsPrioritized = async () => {
    const selectedIds = Array.from(selectedUseCaseIds);
    if (selectedIds.length === 0) return;

    // Filter to only include eligible use cases
    const eligibleIds = selectedIds.filter((id) => {
      const uc = filtered.find((u) => u.id === id);
      return uc && (uc.status === 'Identified' || uc.status === 'Qualified');
    });

    if (eligibleIds.length === 0) {
      setError(t('useCases.selection.noEligible'));
      return;
    }

    setIsUpdatingStatus(true);
    setError(null);

    try {
      // Update each use case status to Prioritized
      await Promise.all(
        eligibleIds.map((id) =>
          useCaseService.update(id, { status: 'Prioritized' as UseCaseStatus })
        )
      );

      // Refresh the list
      const data = await useCaseService.list();
      setUseCases(data);
      setSelectedUseCaseIds(new Set());
    } catch (e: any) {
      setError(e?.message || t('useCases.selection.statusUpdateError'));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <PageLayout
      icon={<ClipboardList className="h-6 w-6" />}
      title={t('useCases.title')}
      subtitle={t('useCases.subtitle')}
    >
      {/* Actions header */}
      <UseCasesActionsHeader
        query={query}
        statusFilter={statusFilter}
        categoryFilter={categoryFilter}
        filterDomainId={filterDomainId}
        valueMin={valueMin}
        valueMax={valueMax}
        complexityMin={complexityMin}
        complexityMax={complexityMax}
        totalUseCases={totalUseCases}
        sortedCategories={sortedCategories}
        domains={domains}
        selectedCount={selectedUseCaseIds.size}
        eligibleCount={eligibleForPrioritization.length}
        isUpdatingStatus={isUpdatingStatus}
        onQueryChange={setQuery}
        onStatusFilterChange={setStatusFilter}
        onCategoryFilterChange={setCategoryFilter}
        onDomainFilterChange={setFilterDomainId}
        onValueMinChange={setValueMin}
        onValueMaxChange={setValueMax}
        onComplexityMinChange={setComplexityMin}
        onComplexityMaxChange={setComplexityMax}
        onNewUseCaseClick={() => navigate('/cas-usage/new')}
        onMarkAsPrioritized={handleMarkAsPrioritized}
        onSelectAllEligible={handleSelectAllEligible}
        onDeselectAll={handleDeselectAll}
        onViewDashboardClick={() => navigate('/cas-usage')}
      />

      {/* UseCase table */}
      <UseCaseTable
        useCases={filtered}
        isLoading={isLoading}
        error={error}
        domainNameById={domainNameById}
        selectedUseCaseIds={selectedUseCaseIds}
        onToggleSelection={handleToggleSelection}
        eligibleForPrioritization={eligibleForPrioritization.map((uc) => uc.id)}
      />
    </PageLayout>
  );
};

export default UseCasesList;
